import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn } from '@angular/router';
import { concat, filter, first, Observable, of, switchMap } from 'rxjs';
import { TranslationsGetLanguageUseCase } from '@/core/domain/translation/usecases/translations-get-language.usecase';
import { HttpClient } from '@angular/common/http';
import { CdnService } from '../services/cdn/cdn.service';
import { Meta, Title } from '@angular/platform-browser';
import _ from 'lodash';
import { Store } from '@ngrx/store';
import { selectTranslation } from '@/data/translation/store/translation.selector';
import { isValid } from '../utils/validation/common.utils';
import {
    isIndexableHost,
    ROBOTS_NOINDEX,
} from '../utils/seo/indexable-host.util';

export const MetaGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
): Observable<boolean> => {
    const getLanguage = inject(TranslationsGetLanguageUseCase);
    const http = inject(HttpClient);
    const title = inject(Title);
    const meta = inject(Meta);
    const store = inject(Store);

    const lang = getLanguage.execute().language;

    const defaultKeyTitle = 'fe_meta_seo_title';
    const defaultKeyDescription = 'fe_meta_seo_description';
    const defaultKeyKeywords = 'fe_meta_seo_keywords';
    const defaultKeyRobots = 'fe_meta_seo_robots';

    let keyTitle = _.get(route, 'data.meta.title', defaultKeyTitle);
    _.forEach(route.params, (item, key) => {
        keyTitle = _.replace(keyTitle, '{' + key + '}', item);
    });

    let keyDescription = _.get(
        route,
        'data.meta.description',
        defaultKeyDescription,
    );
    _.forEach(route.params, (item, key) => {
        keyDescription = _.replace(keyDescription, '{' + key + '}', item);
    });

    const keyKeywords = _.get(route, 'data.meta.keywords', defaultKeyKeywords);
    const keyRobots = _.get(route, 'data.meta.robots', defaultKeyRobots);

    return concat(
        store.select(selectTranslation).pipe(first()),
        http.get(CdnService.url(lang + '.json')),
    ).pipe(
        filter((response) => {
            return (
                Object.keys(response).length > 0 &&
                response.constructor === Object
            );
        }),
        switchMap((response) => {
            const resolve = (key: string, defaultKey: string): string => {
                const value = _.get(response, key, '');
                return isValid(value) ? value : _.get(response, defaultKey, '');
            };

            title.setTitle(resolve(keyTitle, defaultKeyTitle));
            meta.updateTag({
                name: 'description',
                content: resolve(keyDescription, defaultKeyDescription),
            });
            meta.updateTag({
                name: 'keywords',
                content: resolve(keyKeywords, defaultKeyKeywords),
            });
            meta.updateTag({
                name: 'robots',
                content: isIndexableHost()
                    ? resolve(keyRobots, defaultKeyRobots)
                    : ROBOTS_NOINDEX,
            });
            return of(true);
        }),
    );
};
